import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	Container,
	Grid,
	Title,
	Text,
	Button,
	Textarea,
	TextInput,
	Modal,
} from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { fetchProductDetails, submitContactForm } from '../../services/api';
import { Helmet } from 'react-helmet-async';

const ProductDetails: React.FC = () => {
	const { categoryName, fullProduct } = useParams<{
		categoryName: string;
		fullProduct: string;
	}>();

	const [productTitle, id] = fullProduct?.split('-') || ['', ''];
	const [product, setProduct] = useState<any | null>(null);
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		message: '',
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [modalOpened, setModalOpened] = useState(false);
	const [selectedImage, setSelectedImage] = useState<string | null>(null);

	useEffect(() => {
		if (!id || !productTitle) {
			setError('Invalid product URL. Please check the link.');
			setLoading(false);
			return;
		}

		fetchProductDetails(Number(id))
			.then((data) => {
				setProduct(data);
				setLoading(false);
			})
			.catch((err) => {
				console.error('Error fetching product:', err);
				setError('Failed to load product details.');
				setLoading(false);
			});
	}, [id, productTitle]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			await submitContactForm({ ...formData, productId: Number(id) });
			alert('Message sent!');
			setFormData({ name: '', email: '', message: '' });
		} catch (err) {
			console.error('Error submitting contact form:', err);
			alert('Failed to send message. Please try again.');
		}
	};

	if (loading) return <Text>Loading...</Text>;
	if (error) return <Text color="red">{error}</Text>;
	if (!product) return <Text>No product data found.</Text>;

	return (
		<div>
			<Helmet>
				<title>{`${product.productTitle} | Scorpio Classics`}</title>
				<meta
					name="description"
					content={`Discover the ${product.productTitle}, a fully restored vintage classic available at Scorpio Classics.`}
				/>
				<meta
					name="keywords"
					content={`${categoryName}, ${product.productTitle},classic motorbikes, vintage motorcycles, restored bikes, Memorabilia, Metal Automobile Signs, Jukeboxes, Scorpio Classics`}
				/>

				{/* Open Graph for Social Media */}
				<meta
					property="og:title"
					content={`${product.productTitle} - ${product.price.toFixed(
						2
					)} | Scorpio Classics`}
				/>
				<meta
					property="og:description"
					content={`Own a piece of history with the ${
						product.productTitle
					} - ${product.price.toFixed(2)}.`}
				/>
				<meta
					property="og:image"
					content={`https://api.scorpioclassics.com/uploads/${product.imagePaths[0]}`}
				/>
				<meta
					property="og:url"
					content={`https://www.scorpioclassics.com/categories/${categoryName}/${product.productTitle}-${product.productId}`}
				/>

				{/* Twitter Meta Tags */}
				<meta
					name="twitter:title"
					content={`${product.productTitle} - ${product.price.toFixed(2)}`}
				/>
				<meta
					name="twitter:description"
					content={`Buy a classic ${product.productTitle}. Restored to perfection.`}
				/>
				<meta
					name="twitter:image"
					content={`https://api.scorpioclassics.com/uploads/${product.imagePaths[0]}`}
				/>
			</Helmet>
			<Container
				size="lg"
				py="xl">
				<Grid gutter="md">
					<Grid.Col span={12}>
						<Carousel
							loop
							withIndicators>
							{product.imagePaths.map((imagePath: string, index: number) => (
								<img
									key={index}
									src={`https://api.scorpioclassics.com/uploads/${imagePath}`}
									alt={`${product.productTitle} - ${index + 1}`}
									style={{
										width: '100%',
										height: '300px',
										objectFit: 'cover',
										borderRadius: 8,
										cursor: 'pointer',
									}}
									onClick={() => {
										setSelectedImage(
											`https://api.scorpioclassics.com/uploads/${imagePath}`
										);
										setModalOpened(true);
									}}
								/>
							))}
						</Carousel>
					</Grid.Col>
					<Grid.Col span={12}>
						<Title order={2}>{product.productTitle}</Title>
						<Text>{product.productDescription}</Text>
						<Text fw={700}>£{product.price}</Text>
						{product.sold && (
							<Text
								color="red"
								fw={700}
								mt="sm">
								This product has been sold.
							</Text>
						)}
					</Grid.Col>
				</Grid>

				<Title
					order={3}
					mt="xl">
					Contact us about this product
				</Title>
				<form onSubmit={handleSubmit}>
					<Grid gutter="md">
						<Grid.Col span={12}>
							<TextInput
								label="Name"
								value={formData.name}
								onChange={(e) =>
									setFormData({ ...formData, name: e.target.value })
								}
								required
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							<TextInput
								label="Email"
								type="email"
								value={formData.email}
								onChange={(e) =>
									setFormData({ ...formData, email: e.target.value })
								}
								required
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							<Textarea
								label="Message"
								value={formData.message}
								onChange={(e) =>
									setFormData({ ...formData, message: e.target.value })
								}
								required
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							<Button
								type="submit"
								fullWidth>
								Send Message
							</Button>
						</Grid.Col>
					</Grid>
				</form>

				<Modal
					opened={modalOpened}
					onClose={() => setModalOpened(false)}
					title={product.productTitle}
					centered>
					<img
						src={selectedImage || ''}
						alt={product.productTitle}
						style={{ width: '100%', height: 'auto' }}
					/>
				</Modal>
			</Container>

			{/* Structured Data (Schema.org) */}
			<script type="application/ld+json">
				{JSON.stringify({
					'@context': 'https://schema.org/',
					'@type': 'Product',
					name: `${product.productTitle} ${product.price.toFixed(
						2
					)} | Scorpio Classics`,
					image: product.imageUrl,
					description: product.description,
					sku: product.id,
					offers: {
						'@type': 'Offer',
						url: `https://www.scorpioclassics.com/categories/${categoryName}/${product.productTitle}-${product.productId}`,
						priceCurrency: 'GBP',
						price: product.price,
						availability: product.sold
							? 'https://schema.org/SoldOut' // Product is sold
							: 'https://schema.org/InStock', // Product is available
					},
				})}
			</script>
		</div>
	);
};

export default ProductDetails;
